import React from 'react';
import { Box, Grid, Modal, Typography } from '@mui/material';
import { Endpoints, useApiRepo } from '../utils/Api';
import { Colors } from '../utils/Colors';
import { Icons } from '../utils/Icons';
import { CardContainer } from './CardContainer';
import { LinkButton } from './LinkButton';
import { OcdForm } from './OcdForm';
import { OcdTypeButtonCard } from './OcdTypeButtonCard';
import { OcdTypeFilterCard } from './OcdTypeFilterCard';

interface IObsessionType {
  id: string;
  name: string;
  description: string | null;
}

interface ITrigger {
  active: boolean;
  created_ts: Date;
  updated_ts: Date;
  trigger: string;
  user_id: string;
  obsession_id: string;
}

interface ICompulsion {
  active: boolean;
  created_ts: Date;
  updated_ts: Date;
  compulsion: string;
  user_id: string;
  obsession_id: string;
}

export interface IObsession {
  id: string;
  description: string;
  daily_time_consumed_seconds: number | null;
  active: boolean;
  user_id: string;
  intensity: number;
  obsession_type: IObsessionType;
  triggers: ITrigger[];
  compulsions: ICompulsion[];
  created_ts: Date;
  updated_ts: Date;
}

// style coppied from anxiety survey view
const style = {
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4
};

// onFormPost is optional and need to type parameters
export function OcdTypeFilterWithAddButton({
  children,
  onChangeFilter,
  onFormPost
}: any) {
  const { getObjectsFromAPI } = useApiRepo();

  // Modal
  const [open, setOpen] = React.useState(false);
  const modalOpen = () => setOpen(true);
  const modalClose = () => setOpen(false);

  // Filter Options
  const [allFilterOptions, setAllFilterOptions] = React.useState(new Set());
  const [displayFilters, setDisplayFilters] = React.useState<string[]>([]);

  async function getObsessions() {
    const api = await getObjectsFromAPI();
    const res = await api.get(Endpoints.obessions);
    const sortedArray = res.data.data
      .map((item: IObsession) => item.obsession_type.name)
      .sort();
    const filterSet: Set<string> = new Set(sortedArray);
    setAllFilterOptions(filterSet);
  }

  React.useEffect(() => {
    getObsessions();
  }, []);

  React.useEffect(() => {
    onChangeFilter(displayFilters);
  }, [displayFilters]);

  function handleSubmit() {
    onFormPost();
    getObsessions();
  }

  function addFilters(type: string) {
    setDisplayFilters((prevState) => [...prevState, type]);
  }

  function removeFilters(type: string) {
    const filterCopy = [...displayFilters];
    filterCopy.forEach((option) => {
      if (option === type) {
        const i = filterCopy.indexOf(option);
        filterCopy.splice(i, 1);
      }
    });
    setDisplayFilters(filterCopy);
  }

  function clearFilters() {
    setDisplayFilters([]);
  }

  return (
    <Grid container direction="column">
      <Grid container direction="row" spacing={2} style={{ padding: '20px' }}>
        <Grid item xs={12}>
          <Typography align="left" style={{ fontSize: 36 }}>
            OCD Type
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        style={{ height: '100%' }}
      >
        <Modal
          open={open}
          onClose={modalClose}
          aria-labelledby="anxiety-score-results"
          style={{
            overflow: 'scroll',
            maxWidth: '900px',
            margin: 'auto',
            padding: '15px'
          }}
        >
          <Box sx={style}>
            <OcdForm closeForm={modalClose} onFormPost={handleSubmit} />
          </Box>
        </Modal>
      </Grid>
      <CardContainer>
        {Array.from(allFilterOptions).map((ocdType: any) => (
          <Grid
            key={ocdType}
            item
            xs={12}
            md={4}
            lg={2}
            justifyContent="space-between"
          >
            <OcdTypeFilterCard
              type={ocdType}
              addFilter={addFilters}
              removeFilter={removeFilters}
              clear={displayFilters.length === 0}
            />
          </Grid>
        ))}
        <Grid item xs={12} md={4} lg={2} justifyContent="space-between">
          <OcdTypeButtonCard icon={Icons.plusSquare} handleClick={modalOpen} />
        </Grid>
        <Grid item xs={12} justifyContent="center" textAlign="center">
          <LinkButton buttonText="Clear Filters" handleClick={clearFilters} />
        </Grid>
      </CardContainer>
      <Grid
        container
        direction="row"
        spacing={3}
        justifyContent="center"
        style={{ padding: '30px' }}
      >
        <Grid item>
          <Typography
            align="left"
            style={{ fontSize: 20, color: Colors.richBlack }}
          >
            O = Obsession
          </Typography>
        </Grid>
        <Grid item>
          <Typography
            align="left"
            style={{ fontSize: 20, color: Colors.richBlack }}
          >
            C = Compulsions
          </Typography>
        </Grid>
        <Grid item>
          <Typography
            align="left"
            style={{ fontSize: 20, color: Colors.richBlack }}
          >
            T = Triggers
          </Typography>
        </Grid>
      </Grid>
      {children}
    </Grid>
  );
}
